/** What presenter mode needs to know about a screen (a `ScreenDetailed`). */
export type ScreenArea = {
	left: number
	top: number
	width: number
	height: number
	availLeft: number
	availTop: number
	availWidth: number
	availHeight: number
	isInternal?: boolean
	label?: string
}

const sameScreen = (a: ScreenArea, b: ScreenArea): boolean =>
	a === b ||
	(a.left === b.left &&
		a.top === b.top &&
		a.width === b.width &&
		a.height === b.height)

/**
 * The screen the presentation window goes to: never the one the console is
 * on. With two screens that is simply the other one; with more, an external
 * screen (a projector, not the laptop's panel) before an internal one, then
 * the largest.
 */
export const chooseProjector = <S extends ScreenArea>(
	screens: ReadonlyArray<S>,
	current: ScreenArea
): S | undefined => {
	const others = screens.filter(screen => !sameScreen(screen, current))
	if (others.length <= 1) {
		return others[0]
	}
	const external = others.filter(screen => !screen.isInternal)
	const pool = external.length ? external : others
	return [...pool].sort((a, b) => b.width * b.height - a.width * a.height)[0]
}

/**
 * `window.open` features for a popup covering `screen`'s work area. The
 * browser may still clamp it; the presentation window goes full screen there
 * anyway.
 */
export const popupFeaturesOn = (screen: ScreenArea): string =>
	[
		'popup',
		`left=${Math.round(screen.availLeft)}`,
		`top=${Math.round(screen.availTop)}`,
		`width=${Math.round(screen.availWidth)}`,
		`height=${Math.round(screen.availHeight)}`,
	].join(',')

/** Where the presentation window opens when the screens are unknown. */
export const DEFAULT_POPUP_FEATURES = 'popup,width=1280,height=720'
